import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import User from "@mapmycustomers/shared/types/User";
import { Alert } from "@mapmycustomers/ui";

import ExtendedAnalytics from "@app/component/analytics/ExtendedAnalytics";
import Preset, {
  getColors,
} from "@app/scene/dashboard/components/Header/components/Gradient/Preset";
import ViewType from "@app/scene/dashboard/enum/ViewType";
import { saveDashboardSettings } from "@app/scene/dashboard/store/actions";
import { getBoards } from "@app/scene/dashboard/store/selectors";
import canEditBoard from "@app/scene/dashboard/utils/canEditBoard";
import { getMe, getTeamDashboardAlertDismissedAt } from "@app/store/iam";
import { updateMetadata } from "@app/store/iam/actions";
import { RootState } from "@app/store/rootReducer";
import Dashboard from "@app/types/dashboard/Dashboard";
import Scope from "@app/types/dashboard/Scope";
import { parseApiDate } from "@app/util/parsers";

import MemberHeaderContent from "./components/MemberHeaderContent";
import OrganizationHeaderContent from "./components/OrganizationHeaderContent";
import TeamHeaderContent from "./components/TeamHeaderContent";
import styles from "./Header.module.scss";

interface HeaderProps {
  scope: Scope;
  view: ViewType;
}

interface Props extends HeaderProps {
  boards: Dashboard[];
  currentUser: User;
  onDashboardSettingsChange: (payload: {
    scope: Scope;
    settings: Partial<Omit<Dashboard["settings"], "cards" | "overrides">>;
  }) => void;
  onUpdateMetadata: typeof updateMetadata.request;
  teamDashboardAlertDismissedAt?: string;
}

const Header: React.FC<Props> = ({
  boards,
  currentUser,
  onDashboardSettingsChange,
  onUpdateMetadata,
  scope,
  teamDashboardAlertDismissedAt,
  view,
}) => {
  const isMemberScope = !!scope.userId;
  const isTeamScope = !!scope.teamId && !isMemberScope;
  const isOrgScope = !isTeamScope && !isMemberScope;
  const intl = useIntl();

  const canEditHeader = canEditBoard(currentUser, scope, "editSettings");

  const handleGradientChange = useCallback(
    (settings: Partial<Pick<Dashboard["settings"], "gradient" | "gradientFromImage">>) => {
      onDashboardSettingsChange?.({ scope, settings });
    },
    [onDashboardSettingsChange, scope]
  );

  const relatedDashboard = boards.find(
    ({ teamId, userId }) => teamId === scope.teamId && scope.userId === userId
  );
  const teamDashboard = boards.find(
    ({ teamId }) => teamId === scope.teamId && scope.userId === undefined
  );
  const auto = relatedDashboard?.settings.gradientFromImage ?? true;
  const colors = useMemo(
    () => relatedDashboard?.settings.gradient ?? getColors(Preset.BLUE),
    [relatedDashboard?.settings.gradient]
  );

  const dashboardOverriddenAt = teamDashboard?.settings.dashboardOverriddenAt
    ? parseApiDate(teamDashboard.settings.dashboardOverriddenAt)
    : undefined;
  const dismissedAt = teamDashboardAlertDismissedAt
    ? parseApiDate(teamDashboardAlertDismissedAt)
    : undefined;
  const showAlert =
    isTeamScope && !!dashboardOverriddenAt && (!dismissedAt || dashboardOverriddenAt > dismissedAt);

  const handleCloseAlert = useCallback(() => {
    const now = new Date().toISOString();
    onUpdateMetadata({ teamDashboardAlertDismissedAt: now });
  }, [onUpdateMetadata]);

  return (
    <ExtendedAnalytics keyPath="Header">
      {isOrgScope && (
        <OrganizationHeaderContent
          auto={auto}
          colors={colors}
          editable={canEditHeader}
          onGradientChange={handleGradientChange}
          scope={scope}
          view={view}
        />
      )}
      {isTeamScope && (
        <TeamHeaderContent
          auto={auto}
          colors={colors}
          editable={canEditHeader}
          onGradientChange={handleGradientChange}
          scope={scope}
          teamId={scope.teamId!}
          view={view}
        />
      )}
      {isMemberScope && (
        <MemberHeaderContent
          auto={auto}
          colors={colors}
          editable={canEditHeader}
          onGradientChange={handleGradientChange}
          scope={scope}
          userId={scope.userId!}
          view={view}
        />
      )}
      {showAlert && (
        <Alert
          className={styles.alert}
          closable
          message={intl.formatMessage({
            id: "dashboard.header.team.override.alert",
            defaultMessage: "New dashboard configuration applied by admin",
            description: "Dashboard overriden alert info",
          })}
          onClose={handleCloseAlert}
          showIcon
          type="info"
        />
      )}
    </ExtendedAnalytics>
  );
};

const mapStateToProps = (state: RootState) => ({
  boards: getBoards(state),
  currentUser: getMe(state)!,
  teamDashboardAlertDismissedAt: getTeamDashboardAlertDismissedAt(state),
});

const mapDispatchToProps = {
  onDashboardSettingsChange: saveDashboardSettings.request,
  onUpdateMetadata: updateMetadata.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(Header);
